import { refreshApi } from '@/datasources/api/auth'
import type { User } from '@/types'

// Session storage keys — the single definition shared by the axios client, the
// auth store and this module.
export const TOKEN_KEY = 'si-token'
export const REFRESH_KEY = 'si-refresh'
export const USER_KEY = 'si-user'

/**
 * Fired when a refresh returns an updated user — the auth store listens and
 * syncs its Pinia state (B1: roles can change server-side between access
 * tokens; storing to localStorage alone leaves the UI stale until reload).
 */
export const SESSION_USER_EVENT = 'si:session-user'

export type SessionRefresh =
  | { ok: true; user: User | null; accessToken: string; refreshToken: string }
  | { ok: false }

/**
 * Refresh the session from the stored refresh token.
 *
 * This is the ONE refresh implementation: the axios 401 interceptor wraps it
 * with single-flight (concurrent 401s share one call) and proactive callers use
 * it directly. New tokens — and the user, when the API returns one — are
 * persisted here.
 *
 * A failure never clears the session: hard-fail logout belongs to the 401 path,
 * which decides when a refresh failure means "signed out".
 */
export const refreshSession = async (): Promise<SessionRefresh> => {
  const refreshToken = localStorage.getItem(REFRESH_KEY)
  if (!refreshToken) return { ok: false }
  try {
    const response = await refreshApi(refreshToken)
    const code = response.code as number
    if (code !== 200 && code !== 2000 && code !== 2001) return { ok: false }
    const data = response.data
    localStorage.setItem(TOKEN_KEY, data.accessToken)
    localStorage.setItem(REFRESH_KEY, data.refreshToken)
    const user = data.user ?? null
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user))
      window.dispatchEvent(new CustomEvent(SESSION_USER_EVENT, { detail: user }))
    }
    return { ok: true, user, accessToken: data.accessToken, refreshToken: data.refreshToken }
  } catch {
    return { ok: false }
  }
}

/** Drop every stored credential (used by logout and the hard-fail 401 path). */
export const clearSession = (): void => {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_KEY)
  localStorage.removeItem(USER_KEY)
}
